class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('change', this.debouncedOnSubmit);
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = FacetFiltersForm.normalizedParams(
        event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial
      );
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static gridLoadingCount = 0;

  static normalizedParams(searchParams) {
    const params = new URLSearchParams(searchParams || '');
    return [...params.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
  }

  static beginGridLoading() {
    FacetFiltersForm.gridLoadingCount += 1;
    if (FacetFiltersForm.gridLoadingCount === 1) {
      document.dispatchEvent(new CustomEvent('theme:loading:start'));
    }
    const collection = document.getElementById('ProductGridContainer')?.querySelector('.collection');
    if (collection) {
      collection.classList.add('loading', 'is-grid-loading');
    }
    document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    ).forEach((spinner) => spinner.classList.remove('hidden'));
  }

  static finishGridLoading() {
    FacetFiltersForm.gridLoadingCount = Math.max(0, FacetFiltersForm.gridLoadingCount - 1);
    if (FacetFiltersForm.gridLoadingCount > 0) return;

    const collection = document.getElementById('ProductGridContainer')?.querySelector('.collection');
    if (collection) {
      collection.classList.remove('loading', 'is-grid-loading');
    }
    document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    ).forEach((spinner) => spinner.classList.add('hidden'));
    document.dispatchEvent(new CustomEvent('theme:loading:end'));
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const nextParams = FacetFiltersForm.normalizedParams(searchParams);
    if (nextParams === FacetFiltersForm.searchParamsPrev) {
      return;
    }
    FacetFiltersForm.searchParamsPrev = nextParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    FacetFiltersForm.beginGridLoading();
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }
    
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);   
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.updateProductSwiping(html);
      })
      .catch(() => {})
      .finally(() => FacetFiltersForm.finishGridLoading());
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.updateProductSwiping(html);
    FacetFiltersForm.finishGridLoading();
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById('ProductGridContainer');
    const parsed = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    if (!container || !parsed) return;
    container.innerHTML = parsed.innerHTML;
  }

  static updateProductSwiping(html) {
    const gridHost = document.getElementById('ProductGridContainer');
    if (gridHost && typeof initializeSwipeFunctionality === 'function') {
      initializeSwipeFunctionality(gridHost);
    }
    if (gridHost && typeof window.themeScrollRevealInit === 'function') {
      requestAnimationFrame(() => window.themeScrollRevealInit(gridHost));
    }
    if (gridHost && typeof window.themeLoading?.initBlurImages === 'function') {
      requestAnimationFrame(() => window.themeLoading.initBlurImages(gridHost));
    }
  }
  
  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };
    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    const isSortIsolated = !!document.querySelector('.mobile-sort-isolated');

    if (!isSortIsolated) {
    FacetFiltersForm?.renderAdditionalElements?.(parsedHTML);
    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderCollectionToolbar(parsedHTML);
    }

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);
        if (newElementToActivate) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderCollectionToolbar(html) {
    const sourceForm = html.getElementById('FacetCollectionToolbarForm');
    const targetForm = document.getElementById('FacetCollectionToolbarForm');
    if (!sourceForm || !targetForm) return;
    targetForm.innerHTML = sourceForm.innerHTML;

    // Horizontal / vertical layouts keep availability inside other forms.
    html.querySelectorAll('.availability-filter__select').forEach((sourceSelect) => {
      const targetSelect = document.getElementById(sourceSelect.id);
      if (!targetSelect || targetSelect === sourceSelect) return;
      targetSelect.innerHTML = sourceSelect.innerHTML;
      targetSelect.value = sourceSelect.value;
    });
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    if (!event) return;
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.target?.classList?.contains('mobile-facets__checkbox')) {
      const forms = [this.createSearchParams(event.target.closest('form'))];
      const toolbarForm = document.getElementById('FacetCollectionToolbarForm');
      if (toolbarForm) forms.push(this.createSearchParams(toolbarForm));

      const mergedParams = new URLSearchParams(forms.join('&'));
      const sortBys = mergedParams.getAll('sort_by');
      if (sortBys.length > 1) {
        const lastSort = sortBys[sortBys.length - 1];
        mergedParams.delete('sort_by');
        mergedParams.set('sort_by', lastSort);
      }
      [...mergedParams.keys()]
        .filter((key) => key.includes('availability'))
        .forEach((key) => {
          const values = mergedParams.getAll(key).filter((value) => value !== '');
          mergedParams.delete(key);
          if (values.length) {
            mergedParams.set(key, values[values.length - 1]);
          }
        });

      this.onSubmitForm(mergedParams.toString(), event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      // Drawer mode: merge toolbar (availability + sort) with drawer filter form.
      const isDrawerMode = !!document.getElementById('FacetCollectionToolbarForm');

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === 'FacetSortForm' ||
            form.id === 'FacetFiltersForm' ||
            form.id === 'FacetCollectionToolbarForm' ||
            (isDrawerMode && form.id === 'FacetFiltersFormMobile')
          ) {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (
          form.id === 'FacetFiltersFormMobile' ||
          form.id === 'FacetCollectionToolbarForm'
        ) {
          // Mobile drawer changes must keep the top-level Availability selection.
          forms.push(this.createSearchParams(form));
        }
      });
      // Merge params and dedupe sort_by (to avoid two sort_by entries)
      const mergedParams = new URLSearchParams(forms.join('&'));
      const sortBys = mergedParams.getAll('sort_by');
      if (sortBys.length > 1) {
        const lastSort = sortBys[sortBys.length - 1];
        mergedParams.delete('sort_by');
        mergedParams.set('sort_by', lastSort);
      }

      // Availability is single-select. Drop empty "All" values and keep the last non-empty.
      [...mergedParams.keys()]
        .filter((key) => key.includes('availability'))
        .forEach((key) => {
          const values = mergedParams.getAll(key).filter((value) => value !== '');
          mergedParams.delete(key);
          if (values.length) {
            mergedParams.set(key, values[values.length - 1]);
          }
        });

      this.onSubmitForm(mergedParams.toString(), event);
    }
  }


  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const href = event.currentTarget.href;
    const url = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }

}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = FacetFiltersForm.normalizedParams(window.location.search.slice(1));
FacetFiltersForm.searchParamsPrev = FacetFiltersForm.searchParamsInitial;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
    //this.updatePriceLabels();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    //this.updatePriceLabels();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  updatePriceLabels() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    const minLabel = this.querySelector('.pl-min');
    const maxLabel = this.querySelector('.pl-max');
    if (!minInput || !maxInput || !minLabel || !maxLabel) return;

    const minVal = minInput.value !== '' ? Number(minInput.value) : Number(minInput.getAttribute('min') || 0);
    const maxVal = maxInput.value !== '' ? Number(maxInput.value) : Number(maxInput.getAttribute('max') || 0);

    minLabel.textContent = String(minVal);
    maxLabel.textContent = String(maxVal);
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);


document.querySelectorAll('.filter-drawer-category-group .accordion-title').forEach((el) => {
  // Enter fires on keydown (like native)
  el.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      el.click();
    }
    if (e.key === ' ') e.preventDefault();
  });

  el.addEventListener('keyup', (e) => {
    if (e.key === ' ') {
      e.preventDefault();
      el.click();
    }
  });
});

document.addEventListener('click', function (event) {
  const closeBtn = event.target.closest('.mobile-facets-close-button');
  if (!closeBtn) return;
  event.preventDefault();
  event.stopPropagation();
});

